import { useState, useCallback, useReducer, useEffect } from "react";

import Settings from "@classes/Settings";
import User from "@classes/User";
import api from "@helpers/api";

import addCustomerModalReducer, { initialState } from "./optFormReducer";
import validateFields from "@helpers/formValidator";
import { formatSSN , setExtendedSessionTimeout } from "@helpers/utils";

const TIMEOUT_DIALOG_TIME_WINDOW_MS = import.meta.env.VITE_TIMEOUT_DIALOG_TIME_WINDOW_MS || 21600000; // Default to 6 hours if not defined in env file

const useOtpForm = props => {
  const { open, toggleModal, actions, history } = props;
  const [state, dispatch] = useReducer(addCustomerModalReducer, initialState);

  const canSubmitForm =
    !!(!state.otpRequested && state.personalNumber && state.medium) ||
    !!(state.otpRequested && state.otpCode.length === 6);

  const toggle = () => {
    dispatch({
      type: "reset"
    });
    return toggleModal();
  };

  const isValidInput = (fieldName, value) => {
    return (
      value.length > 0 && (!state.inputErrors || !state?.inputErrors[fieldName])
    );
  };

  const handleInputChange = async e => {
    e.preventDefault();
    const key = e.target.name;
    let value = e.target.value;

    dispatch({
      type: "handleInput",
      field: key,
      payload: value
    });

    const formData = {
      ...state,
      [key]: value
    };

    // Only validate personalNumber field
    if (key !== "personalNumber") {
      return;
    }

    let inputErrors = validateFields({
      personalNumber: formData.personalNumber
    });

    return dispatch({
      type: "handleInputErrors",
      payload: inputErrors || {}
    });
  };

  const handleSubmit = async e => {
    dispatch({
      type: "handleServerError",
      payload: null
    });

    dispatch({
      type: "fetching",
      payload: true
    });

    try {
      if (!state.otpRequested) {
        // REQUEST OTP
        const otp = await api(
          `/partner/otp/request`,
          {
            personalNumber: formatSSN(state.personalNumber),
            medium: state.medium
          },
          {},
          true
        );

        if (!otp) {
          return dispatch({
            type: "handleServerError",
            payload: "Något gick fel. Vänligen kontakta support."
          });
        }

        if (!otp.success) {
          const msg =
            otp?.error?.message == "OTP_ON_COOLDOWN"
              ? "En OTP har redan skickats. Vänligen vänta innan du begär en ny."
              : otp?.error?.message ||
                "Något gick fel. Vänligen kontakta support.";

          return dispatch({
            type: "handleServerError",
            payload: msg
          });
        }

        return dispatch({
          type: "handleOtpSent"
        });
      } else {
        // Set app to busy state
        actions.setAppState({ busy: true });

        // VERIFY OTP
        const verifyOtp = await api(
          `/partner/otp/verify`,
          {
            personalNumber: formatSSN(state.personalNumber),
            otpCode: state.otpCode
          },
          {},
          true
        );

        if (!verifyOtp) {
          return dispatch({
            type: "handleServerError",
            payload: "Ogiltig OTP-kod."
          });
        }

        if (!verifyOtp.success) {
          let msg;

          const serverMsg = verifyOtp?.error?.message;
          switch (serverMsg) {
            case "INVALID_OTP":
              msg = "Ogiltig OTP-kod.";
              break;
            case "OTP_ON_COOLDOWN":
              msg = "OTP-koden är på cooldown.";
              break;
            case "MAX_ATTEMPTS_REACHED":
              msg = "Maximala antalet försök har nåtts.";
              break;
            default:
              msg = serverMsg || "Något gick fel. Vänligen kontakta support.";
              break;
          }

          actions.setAppState({ busy: false });
          return dispatch({
            type: "handleServerError",
            payload: msg
          });
        }

        const token = verifyOtp?.result?.token;
        if (!token) {
          actions.setAppState({ busy: false });
          return dispatch({
            type: "handleServerError",
            payload: "Något gick fel. Vänligen kontakta support."
          });
        }

        // Save token to settings
        Settings.set("AUTH_TOKEN", token);

        const { partner, advisor } = await api("/auth/data");
        if (partner) {
          actions.setAppState({ busy: false });
          actions.setUser(new User(partner));
          actions.setAdvisor(new User(advisor));

          // Extended session timeout, sets extended session timeout to true if app has been running for the defined time window
          setExtendedSessionTimeout( TIMEOUT_DIALOG_TIME_WINDOW_MS );

          // Redirect
          return history.push("/cob");
        } else {
          Settings.clear();
          return history.push("/partner-not-found");
        }
      }
    } catch (err) {
      console.log(err, "err");
      actions.setAppState({ busy: false });
      const msg = err?.response?.data?.msg;

      return dispatch({
        type: "handleServerError",
        payload: msg || err.msg || err.message || "Unknown error."
      });
    }
  };

  return {
    open,
    state,
    toggle,
    isValidInput,
    canSubmitForm,
    handleInputChange,
    // handleLookUp,
    handleSubmit
  };
};

export default useOtpForm;
